/**
 * Featured Articles AJAX Tag Filter
 * Loads articles from tagged blog URLs to bypass the 50 article limit
 */

if (!window.FeaturedArticlesAjaxFilter) {
  window.FeaturedArticlesAjaxFilter = class {
    constructor(placeholder) {
      // Prevent double initialization
      if (placeholder.dataset.ajaxInitialized === 'true') {
        return;
      }
      placeholder.dataset.ajaxInitialized = 'true';

    this.placeholder = placeholder;
    this.parentUl = placeholder.parentElement;

    // Validate parent UL exists
    if (!this.parentUl || this.parentUl.tagName !== 'UL') {
      console.error('[AjaxFilter] Invalid parent element, expected UL');
      this.showError();
      return;
    }

    this.blogHandle = placeholder.dataset.blogHandle;
    this.filterTag = placeholder.dataset.filterTag;
    this.itemsLimit = parseInt(placeholder.dataset.itemsLimit) || 4;
    this.startIndex = parseInt(placeholder.dataset.articleStartIndex) || 0;
    this.sectionId = placeholder.dataset.sectionId;

    // Validate required data
    if (!this.blogHandle || !this.filterTag) {
      console.error('[AjaxFilter] Missing required data:', { blogHandle: this.blogHandle, filterTag: this.filterTag });
      this.showError();
      return;
    }

    // Validate limits
    if (this.itemsLimit < 1 || this.itemsLimit > 50) {
      this.itemsLimit = 4;
    }

    this.init();
  }

  init() {
    this.loadArticles();
  }

  async loadArticles() {
    // filterTag is already handleized from Liquid, no need to encode again
    const taggedUrl = `/blogs/${encodeURIComponent(this.blogHandle)}/tagged/${this.filterTag}`;

    try {
      const response = await fetch(taggedUrl);

      if (!response.ok) {
        if (response.status === 404) {
          this.showNoResults();
          return;
        }
        // Show friendly error for server errors
        if (response.status >= 500) {
          this.showError();
          return;
        }
        // For other errors (403, 429, etc)
        this.showError();
        return;
      }

      const html = await response.text();

      if (!html || html.length === 0) {
        this.showError();
        return;
      }

      this.parseAndRenderArticles(html);
    } catch (err) {
      // Log technical details to console for debugging
      console.error('[AjaxFilter] Failed to load articles:', err);
      // Show user-friendly message
      this.showError('network');
    }
  }

  parseAndRenderArticles(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Find article cards in the fetched page (from main-blog section)
    const articleCards = doc.querySelectorAll('.blog-articles__article');

    if (articleCards.length === 0) {
      this.showNoResults();
      return;
    }

    // Get layout settings
    const gridElement = this.parentUl;
    const layoutType = gridElement?.classList.contains('featured-articles__grid--asymmetric') ? 'asymmetric' : 'grid';
    const isSlider = gridElement?.classList.contains('slider') || false;

    // Auto-detect image height class from existing cards
    const imageHeightClass = this.detectImageHeightClass();

    // Create document fragment for better performance
    const fragment = document.createDocumentFragment();

    // Track unique articles by URL to prevent duplicates
    const seenUrls = new Set();

    // Render limited number of articles
    let renderedCount = 0;
    articleCards.forEach((articleCard) => {
      if (renderedCount >= this.itemsLimit) return;

      const articleData = this.extractArticleData(articleCard);
      if (!articleData) return;

      // Skip if we've already seen this URL (prevent duplicates)
      if (seenUrls.has(articleData.url)) return;
      seenUrls.add(articleData.url);

      const isLargeCard = layoutType === 'asymmetric' && renderedCount === 0 && this.startIndex === 0;
      const li = this.createArticleElement(articleData, isLargeCard, isSlider, renderedCount, imageHeightClass);

      fragment.appendChild(li);
      renderedCount++;
    });

    if (renderedCount === 0) {
      this.showNoResults();
      this.hideSliderButtons();
    } else {
      // Insert articles before placeholder, then remove placeholder
      this.parentUl.insertBefore(fragment, this.placeholder);
      const placeholderToRemove = this.placeholder;
      this.placeholder = null; // Clear reference before removal
      placeholderToRemove.remove();

      // Re-initialize slider component after adding new items
      this.reinitSlider();

      // Trigger animation for newly added items
      this.triggerAnimation();
    }
  }

  reinitSlider() {
    window.FeaturedArticlesUtils.reinitSlider(this.parentUl);
  }

  triggerAnimation() {
    window.FeaturedArticlesUtils.triggerAnimation(this.parentUl);
  }

  hideSliderButtons() {
    window.FeaturedArticlesUtils.hideSliderButtons(this.parentUl);
  }

  detectImageHeightClass() {
    return window.FeaturedArticlesUtils.detectImageHeightClass(this.parentUl);
  }

  extractArticleData(articleCard) {
    return window.FeaturedArticlesUtils.extractArticleData(articleCard);
  }

  createArticleElement(data, isLargeCard, isSlider, index, imageHeightClass = 'adapt') {
    const li = document.createElement('li');
    li.className = `featured-articles__item${isLargeCard ? ' featured-articles__item--large' : ' featured-articles__item--small'} grid__item${isSlider ? ' slider__slide slider__slide--full-width' : ''} appear-animate-item-in-list`;
    li.id = `Slide-${this.sectionId}-${this.startIndex + index + 1}`;

    li.innerHTML = window.FeaturedArticlesUtils.createArticleCardHTML(data, {
      isLargeCard,
      imageHeight: imageHeightClass,
      showCategoryBadge: true,
      showDatePrefix: true
    });

    return li;
  }

  showNoResults() {
    if (!this.placeholder) return;

    const escapeHtml = window.FeaturedArticlesUtils.escapeHtml;
    this.placeholder.innerHTML = `
      <div style="text-align: center; padding: 4rem 2rem;">
        <p>No articles found with tag "${escapeHtml(this.filterTag.replace(/-/g, ' '))}"</p>
        <p style="font-size: 0.9em; color: #666; margin-top: 1rem;">Try checking if articles with this tag exist in the blog.</p>
      </div>`;
    this.placeholder.style.gridColumn = '1 / -1';
  }

  showError() {
    if (!this.placeholder) return;

    this.placeholder.innerHTML = `
      <div style="text-align: center; padding: 4rem 2rem;">
        <p style="font-size: 1em; color: rgba(var(--color-foreground), 0.6);">
          Unable to load articles at this time.
        </p>
      </div>`;
    this.placeholder.style.gridColumn = '1 / -1';
    this.hideSliderButtons();
  }
  }; // End of FeaturedArticlesAjaxFilter class
} // End of if (!window.FeaturedArticlesAjaxFilter)

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  const ajaxPlaceholders = document.querySelectorAll('.featured-articles__ajax-placeholder');
  ajaxPlaceholders.forEach(placeholder => {
    new window.FeaturedArticlesAjaxFilter(placeholder);
  });
});
